import { useEffect, useRef, useState } from 'react';
import { copyToClipboard } from '../../lib/clipboard';
import { PinBadge } from '../../components/ui/PinBadge';
import type { HeadlinePinPosition } from '../../types/campaign';

// P2b-3 Build View commit 4: shared primitives between
// <VariationsListPopover> (popover wrapper around the cell preview)
// and <VariationsListInline> (rendered inline inside Build View
// content sections). Same card styling, same per-card "Copied!"
// feedback rules, same Copy-all newline-join behaviour.

const TOAST_MS = 1500;

export function VariationCard({
  index,
  value,
  pinPosition,
}: {
  index: number;
  value: string;
  // Pinned Headlines spec §2.3: optional pin slot rendered between
  // the variation number and the Copy button. Copy still copies just
  // the text — pin metadata never enters the clipboard string.
  pinPosition?: HeadlinePinPosition;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const handleCopy = async () => {
    const ok = await copyToClipboard(value);
    if (!ok) return;
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), TOAST_MS);
  };

  return (
    <li className="rounded-md border border-slate-200 bg-white px-3 py-2.5">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="inline-flex items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-700">
            Variation {index}
          </span>
          <PinBadge pinPosition={pinPosition ?? null} />
        </span>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={`Copy variation ${index}`}
          title={copied ? 'Copied!' : 'Copy this variation'}
          className={
            'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded transition-colors ' +
            (copied
              ? 'bg-emerald-100 text-emerald-700'
              : 'text-slate-500 hover:bg-slate-100')
          }
        >
          {copied ? (
            <>
              <span aria-hidden>✓</span> Copied!
            </>
          ) : (
            <>
              <span aria-hidden>⎘</span> Copy
            </>
          )}
        </button>
      </div>
      <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">
        {value}
      </p>
    </li>
  );
}

export function CopyAllButton({ variations }: { variations: string[] }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const handleCopy = async () => {
    const ok = await copyToClipboard(variations.join('\n'));
    if (!ok) return;
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), TOAST_MS);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label="Copy all variations"
      title={copied ? 'Copied!' : 'Copy all variations (newline-joined)'}
      className={
        'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded transition-colors ' +
        (copied
          ? 'bg-emerald-100 text-emerald-700'
          : 'text-slate-500 hover:bg-slate-100 border border-slate-200')
      }
    >
      {copied ? (
        <>
          <span aria-hidden>✓</span> Copied all
        </>
      ) : (
        <>
          <span aria-hidden>⎘</span> Copy all
        </>
      )}
    </button>
  );
}
